'use client';

import {
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { useContext, useState } from 'react';

import { Button, HeroIcon, Typography } from '@/components/atoms';
import { Sheet, LikeButton } from '@/components/molecules';
import { useAuthContext } from '@/lib/contexts/auth';
import { FavoritesContext } from '@/lib/contexts/favouriting';
import { useDialog } from '@/lib/hooks/auth/useDialog';

import { AddToPlaylist } from './AddToPlaylist';
import { ApprovalModal } from './ApprovalModal';
import { CreatePlaylistForm } from './CreatePlaylist';

export interface TrackOptionsMenuProps {
  drawerOpen: boolean;
  drawerOpenAction: (open: boolean) => void;
  trackId: number;
  isPersonalPlaylistTrack?: boolean;
  playlistId?: number;
  onTrackRemoved?: () => void;
}

export const TrackOptionsMenu = ({
  drawerOpen,
  drawerOpenAction,
  trackId,
  isPersonalPlaylistTrack,
  playlistId,
  onTrackRemoved,
}: TrackOptionsMenuProps) => {
  const t = useTranslations();
  const { isAuthenticated } = useAuthContext();
  const { openDialog } = useDialog();
  const [isAddOptionsOpen, setIsAddOptionsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isCreatingNewPlaylist, setIsCreatingNewPlaylist] = useState(false);
  const favoritesContext = useContext(FavoritesContext);
  if (!favoritesContext) {
    throw new Error('Addmenu must be wrapped in provider');
  }
  const {
    addTrackToUserPlaylist,
    userPlaylists,
    createUserPlaylistAndStore,
    fetchUserPlaylists,
    removeTrackFromUserPlaylist,
  } = favoritesContext;
  const handleDeleteTrackBtn = () => {
    setShowDeleteModal(true);
    drawerOpenAction(false);
  };
  const confirmRemoveTrack = async () => {
    if (playlistId) {
      await removeTrackFromUserPlaylist(playlistId, trackId);
      onTrackRemoved?.();
    }
    drawerOpenAction(false);
    setShowDeleteModal(false);
  };
  const handleBackBtn = () => {
    if (isCreatingNewPlaylist) {
      setIsCreatingNewPlaylist(false);
    } else {
      setIsAddOptionsOpen(false);
    }
  };

  const handleAddButtonClick = () => {
    if (!isAuthenticated) {
      openDialog('auth');
      return;
    }
    setIsAddOptionsOpen(true);
  };
  return (
    <>
      <Sheet.Root
        isOpen={drawerOpen}
        onOpenChange={drawerOpenAction}
        aria-labelledby="test"
        className="flex flex-col w-full"
      >
        <Sheet.Header>
          <Sheet.Title>
            {isAddOptionsOpen ? (
              <Button variant={'ghost'} onClick={handleBackBtn}>
                <HeroIcon icon={ChevronLeftIcon} size="medium" />
                <div className="flex flex-col justify-center">
                  {t('B2B_LIBRARY_SEE_ALL_BUTTON_BACK')}
                </div>
              </Button>
            ) : (
              ''
            )}
          </Sheet.Title>
          <Sheet.Description className="sr-only">
            {t('MORE_OPTIONS')}
          </Sheet.Description>
          <Sheet.Close />
        </Sheet.Header>
        <Sheet.Body className="flex overflow-auto flex-col items-start gap-2 w-full">
          {!isAddOptionsOpen && (
            <>
              <LikeButton
                contentId={trackId}
                contentType="Track"
                className="md:w-48 flex items-center gap-2"
                defaultContent={t('FOCAL_LIKE')}
                likedContent={t('FOCAL_LIKED')}
              />
              <Button variant={'ghost'} onClick={handleAddButtonClick}>
                <HeroIcon icon={PlusIcon} size="medium" />
                <div className="flex flex-col justify-center">
                  <Typography.Text
                    variant="paragraph"
                    size="md"
                    weight="medium"
                  >
                    {t('SAVE_IN_OTHER_PLAYLIST')}
                  </Typography.Text>
                </div>
              </Button>
            </>
          )}
          {isAddOptionsOpen && (
            <div className="flex flex-col w-full">
              {!isCreatingNewPlaylist ? (
                <AddToPlaylist
                  trackId={trackId}
                  userPlaylists={userPlaylists}
                  addTrackToUserPlaylist={addTrackToUserPlaylist}
                  onDone={() => drawerOpenAction(false)}
                  onCreatePlaylistClick={() => setIsCreatingNewPlaylist(true)}
                />
              ) : (
                <CreatePlaylistForm
                  trackId={trackId}
                  createUserPlaylistAndStore={createUserPlaylistAndStore}
                  addTrackToUserPlaylist={addTrackToUserPlaylist}
                  fetchUserPlaylists={fetchUserPlaylists}
                  onDone={() => drawerOpenAction(false)}
                />
              )}
            </div>
          )}
          {isPersonalPlaylistTrack && !isAddOptionsOpen && (
            <Button variant={'ghost'} onClick={handleDeleteTrackBtn}>
              <HeroIcon icon={TrashIcon} size="medium" />
              <div className="flex flex-col justify-center">
                <Typography.Text variant="paragraph" size="md" weight="medium">
                  {t('MENUMORE_REMOVE_FROM_PLAYLIST')}
                </Typography.Text>
              </div>
            </Button>
          )}
        </Sheet.Body>
      </Sheet.Root>
      {showDeleteModal && (
        <ApprovalModal
          modalOpen={showDeleteModal}
          modalOpenAction={setShowDeleteModal}
          modalTitle={t('EXPANDED_PLAYER_SETTINGS')}
          modalSubtitle={t('MY_PLAYLISTS_DELETE_TRACK_POPUP')}
          modalCTA={t('MY_PLAYLIST_DELETE_TRACK_CTA')}
          onConfirm={confirmRemoveTrack}
        />
      )}
    </>
  );
};
